import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Chip,
  Paper,
  Button,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import {
  Google as GoogleIcon,
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon
} from '@mui/icons-material';

interface GoogleMCPNavigationProps {
  currentPage?: string;
  showQuickActions?: boolean;
}

const GoogleMCPNavigation: React.FC<GoogleMCPNavigationProps> = ({ 
  currentPage,
  showQuickActions = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const mcpPages = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      path: '/admin/google-mcp',
      icon: <DashboardIcon />,
      description: 'Visão geral'
    },
    {
      id: 'campaigns',
      title: 'Campanhas',
      path: '/admin/google-mcp/campaigns',
      icon: <CampaignIcon />,
      description: 'Gerenciar campanhas'
    },
    {
      id: 'leads',
      title: 'Leads',
      path: '/admin/google-mcp/leads',
      icon: <TrendingUpIcon />,
      description: 'Leads capturados'
    },
    {
      id: 'analytics',
      title: 'Analytics',
      path: '/admin/google-mcp/analytics',
      icon: <AnalyticsIcon />,
      description: 'Métricas e relatórios'
    },
    {
      id: 'alerts',
      title: 'Alertas',
      path: '/admin/google-mcp/alerts',
      icon: <NotificationsIcon />,
      description: 'Configurar alertas'
    },
    {
      id: 'settings',
      title: 'Configurações',
      path: '/admin/google-mcp/settings',
      icon: <SettingsIcon />,
      description: 'Configurações do sistema'
    }
  ];

  const getCurrentPageInfo = () => {
    const path = location.pathname;
    if (path === '/admin/google-mcp') return mcpPages[0];
    return mcpPages.find(page => path.includes(page.id)) || mcpPages[0];
  };

  const currentPageInfo = getCurrentPageInfo();

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      {
        label: 'Admin',
        path: '/admin',
        icon: <HomeIcon sx={{ fontSize: 16 }} />
      },
      {
        label: 'Google MCP',
        path: '/admin/google-mcp',
        icon: <GoogleIcon sx={{ fontSize: 16 }} />
      }
    ];

    if (currentPageInfo.id !== 'dashboard') {
      breadcrumbs.push({
        label: currentPageInfo.title,
        path: currentPageInfo.path,
        icon: React.cloneElement(currentPageInfo.icon, { sx: { fontSize: 16 } })
      });
    }

    return breadcrumbs;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          {getBreadcrumbs().map((breadcrumb, index) => {
            const isLast = index === getBreadcrumbs().length - 1;
            
            return isLast ? (
              <Box 
                key={breadcrumb.path}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: 'text.primary'
                }}
              >
                {breadcrumb.icon}
                <Typography 
                  variant="body2" 
                  sx={{ ml: 0.5, fontWeight: 'medium' }}
                >
                  {breadcrumb.label}
                </Typography>
              </Box>
            ) : (
              <Link
                key={breadcrumb.path}
                underline="hover"
                color="inherit"
                onClick={() => handleNavigation(breadcrumb.path)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {breadcrumb.icon}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  {breadcrumb.label}
                </Typography>
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>

      {/* Header da Página Atual */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 2
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              mr: 2
            }}>
              {currentPageInfo.icon}
            </Box>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold">
                {currentPageInfo.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentPageInfo.description}
              </Typography>
            </Box>
          </Box>
          
          <Chip 
            icon={<GoogleIcon />}
            label="Google MCP"
            variant="outlined"
            color="primary"
            size="small"
          />
        </Box>

        {/* Ações Rápidas */}
        {showQuickActions && (
          <Box>
            <ButtonGroup variant="outlined" size="small">
              {mcpPages.slice(0, 4).map((page) => {
                const isActive = location.pathname === page.path;
                return (
                  <Tooltip key={page.id} title={page.description}>
                    <Button
                      variant={isActive ? 'contained' : 'outlined'}
                      onClick={() => handleNavigation(page.path)}
                      startIcon={page.icon}
                      sx={{ 
                        minWidth: 'auto',
                        px: 1.5,
                        '& .MuiButton-startIcon': {
                          mr: { xs: 0, sm: 1 }
                        }
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: { xs: 'none', sm: 'block' },
                          fontWeight: isActive ? 'bold' : 'normal'
                        }}
                      >
                        {page.title}
                      </Typography>
                    </Button>
                  </Tooltip>
                );
              })}
            </ButtonGroup>
          </Box>
        )}
      </Box>

      {/* Navegação Horizontal */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 1, 
          display: 'flex', 
          gap: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: 2,
          },
        }}
      >
        {mcpPages.map((page) => {
          const isActive = location.pathname === page.path;
          return (
            <Button
              key={page.id}
              variant={isActive ? 'contained' : 'text'}
              size="small"
              startIcon={page.icon}
              onClick={() => handleNavigation(page.path)}
              sx={{
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                px: 2,
                py: 1,
                borderRadius: 1,
                fontWeight: isActive ? 'bold' : 'normal',
                '&:hover': {
                  backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              {page.title}
            </Button>
          );
        })}
      </Paper>
    </Box>
  );
};

export default GoogleMCPNavigation;